import { TestTube2, Beaker, Home, AlertTriangle, CreditCard } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    documentSubtype: 'BOOKING_RECEIPT' | 'SAMPLE_COLLECTION' | 'TEST_INVOICE' | 'HOME_COLLECTION' | 'CRITICAL_ALERT' | 'VERIFICATION_CERTIFICATE';
    labName: string;
    labNhcId?: string;
    sampleId?: string;
    reportId?: string;
    bookingId?: string;
    pathologistName?: string;
    pathologistId?: string;
    tests: { name: string; sampleType?: string; price?: number }[];
    collectionDate?: string;
    collectionTime?: string;
    collectionMode?: string;
    homeAddress?: string;
    phlebotomistName?: string;
    reportDate?: string;
    criticalTest?: string;
    criticalValue?: string;
    criticalRange?: string;
    subtotal?: number;
    homeCollectionFee?: number;
    discount?: number;
    gst?: number;
    total?: number;
    amountPaid?: number;
    paymentMode?: string;
    paymentStatus?: string;
    notes?: string[];
    signatories: { name: string; role: string; nhcId?: string; licenseNo?: string; institution?: string }[];
  };
}

function fmt(n: number) { return `Rs. ${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`; }

const SUBTYPE_LABELS: Record<string, string> = {
  BOOKING_RECEIPT: 'Lab Booking Receipt',
  SAMPLE_COLLECTION: 'Lab Sample Collection Receipt',
  TEST_INVOICE: 'Lab Test Invoice',
  HOME_COLLECTION: 'Home Sample Collection Receipt',
  CRITICAL_ALERT: 'Critical Lab Alert Document',
  VERIFICATION_CERTIFICATE: 'Lab Report Verification Certificate',
};

export default function LabDocumentTemplate({ data }: Props) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-1.5 mb-1">
        {data.documentSubtype === 'HOME_COLLECTION' ? <Home className="w-3.5 h-3.5 text-gray-700" /> :
         data.documentSubtype === 'CRITICAL_ALERT' ? <AlertTriangle className="w-3.5 h-3.5 text-red-600" /> :
         <TestTube2 className="w-3.5 h-3.5 text-gray-700" />}
        <span className="text-[9px] font-bold uppercase tracking-wider text-gray-700">
          {SUBTYPE_LABELS[data.documentSubtype] || 'Lab Document'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[9px]">
        <div><span className="text-gray-500">Lab:</span> <span className="text-gray-800 font-medium">{data.labName}</span></div>
        {data.labNhcId && <div><span className="text-gray-500">Lab NHC:</span> <span className="font-mono text-gray-700">{data.labNhcId}</span></div>}
        {data.bookingId && <div><span className="text-gray-500">Booking ID:</span> <span className="font-mono text-gray-800">{data.bookingId}</span></div>}
        {data.sampleId && <div><span className="text-gray-500">Sample ID:</span> <span className="font-mono text-gray-800">{data.sampleId}</span></div>}
        {data.reportId && <div><span className="text-gray-500">Report ID:</span> <span className="font-mono text-gray-800">{data.reportId}</span></div>}
        {data.pathologistName && <div><span className="text-gray-500">Pathologist:</span> <span className="text-gray-800">{data.pathologistName}</span></div>}
        {data.collectionDate && <div><span className="text-gray-500">Collection:</span> <span className="text-gray-800">{data.collectionDate} {data.collectionTime || ''}</span></div>}
        {data.reportDate && <div><span className="text-gray-500">Report Date:</span> <span className="text-gray-800">{data.reportDate}</span></div>}
        {data.collectionMode && <div><span className="text-gray-500">Mode:</span> <span className="text-gray-800 capitalize">{data.collectionMode.toLowerCase()}</span></div>}
      </div>

      {data.homeAddress && (
        <div className="bg-gray-50 px-3 py-1.5 border border-gray-200 rounded text-[9px] flex items-center gap-1.5">
          <Home className="w-3 h-3 text-gray-500" />
          <span className="text-gray-500">Address:</span> <span className="text-gray-800">{data.homeAddress}</span>
          {data.phlebotomistName && <span className="text-gray-500 ml-2">Phlebotomist: <span className="text-gray-800">{data.phlebotomistName}</span></span>}
        </div>
      )}

      {data.documentSubtype === 'CRITICAL_ALERT' && data.criticalTest && (
        <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-[9px]">
          <p className="text-red-800 font-bold flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Critical Value Alert</p>
          <div className="grid grid-cols-3 gap-2 mt-1">
            <div><span className="text-red-600">Test:</span> <span className="text-red-900 font-medium">{data.criticalTest}</span></div>
            <div><span className="text-red-600">Value:</span> <span className="text-red-900 font-bold">{data.criticalValue}</span></div>
            <div><span className="text-red-600">Range:</span> <span className="text-red-900">{data.criticalRange}</span></div>
          </div>
        </div>
      )}

      {/* Test Table */}
      {data.tests.length > 0 && (
        <div>
          <p className="text-[8px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1"><Beaker className="w-3 h-3" /> Tests</p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[9px]">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">Test Name</th>
                  {data.tests.some(t => t.sampleType) && <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">Sample</th>}
                  {data.tests.some(t => t.price !== undefined) && <th className="text-right font-bold text-gray-700 py-1 px-1.5 border border-gray-300">Price</th>}
                </tr>
              </thead>
              <tbody>
                {data.tests.map((t, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="py-1 px-1.5 border border-gray-200 text-gray-800 font-medium">{t.name}</td>
                    {data.tests.some(x => x.sampleType) && <td className="py-1 px-1.5 border border-gray-200 text-gray-600">{t.sampleType || '—'}</td>}
                    {data.tests.some(x => x.price !== undefined) && <td className="py-1 px-1.5 border border-gray-200 text-gray-800 text-right font-mono">{t.price !== undefined ? fmt(t.price) : '—'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Totals */}
      {data.total !== undefined && (
        <div className="flex justify-end">
          <div className="w-52 bg-gray-50 border border-gray-200 rounded text-[9px]">
            {data.subtotal !== undefined && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Subtotal</span><span className="font-mono text-gray-800">{fmt(data.subtotal)}</span></div>}
            {(data.homeCollectionFee ?? 0) > 0 && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Home Collection</span><span className="font-mono text-gray-800">{fmt(data.homeCollectionFee!)}</span></div>}
            {(data.discount ?? 0) > 0 && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Discount</span><span className="font-mono text-green-700">- {fmt(data.discount!)}</span></div>}
            {(data.gst ?? 0) > 0 && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">GST</span><span className="font-mono text-gray-800">{fmt(data.gst!)}</span></div>}
            <div className="flex justify-between py-1 px-2.5 bg-gray-100 font-bold border-b border-gray-300"><span className="text-gray-800">Total</span><span className="font-mono text-gray-900">{fmt(data.total)}</span></div>
            {data.amountPaid !== undefined && <div className="flex justify-between py-0.5 px-2.5"><span className="text-gray-500">Paid</span><span className="font-mono text-green-700">{fmt(data.amountPaid)}</span></div>}
          </div>
        </div>
      )}

      {data.documentSubtype === 'CRITICAL_ALERT' && (
        <div className="bg-amber-50 border border-amber-200 rounded px-3 py-1.5 text-[8px] text-amber-800">
          Placeholder — Critical lab alerts require real-time notification and clinical escalation system integration.
        </div>
      )}

      {data.notes && data.notes.length > 0 && (
        <ul className="list-disc list-inside space-y-0.5 text-[9px] text-gray-700 bg-gray-50 px-3 py-1.5 border border-gray-200 rounded">
          {data.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}

      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
